'use client';

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle, BarChart3, Zap, Droplet, Leaf, Cloud, Sun, LineChart, CloudRain } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function AgritechPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const solutions = [
    {
      title: "Precision Farming AI",
      icon: Leaf,
      description: "Advanced AI algorithms that analyze soil quality, weather patterns, and crop health to optimize growing conditions and increase yields.",
      benefits: [
        "25-40% increase in crop yield",
        "Reduction of water usage by up to 30%",
        "Early pest and disease detection",
        "Optimized resource allocation across fields"
      ]
    },
    {
      title: "Smart Irrigation Systems",
      icon: Droplet,
      description: "IoT-powered irrigation systems that deliver precise amounts of water based on real-time soil moisture data, weather forecasts, and crop requirements.",
      benefits: [
        "Water conservation up to 50% compared to traditional methods", 
        "Automated scheduling based on weather predictions",
        "Smartphone control and monitoring capabilities",
        "Prevention of over/under watering conditions"
      ]
    },
    {
      title: "Crop Yield Prediction",
      icon: LineChart,
      description: "Machine learning models that forecast harvest yields with high accuracy by analyzing historical data, current growing conditions, and satellite imagery.",
      benefits: [
        "90% average accuracy in yield predictions",
        "Better inventory and supply chain planning", 
        "Improved financial forecasting capabilities",
        "Early identification of potential yield issues"
      ]
    },
    {
      title: "Weather Analytics Platform",
      icon: CloudRain,
      description: "Hyperlocal weather forecasting combined with agricultural impact models to help farmers make critical timing decisions.",
      benefits: [
        "Micro-climate monitoring at field level", 
        "Frost and extreme weather alerts",
        "Planting and harvesting timing optimization",
        "Integration with farm management systems"
      ]
    },
    {
      title: "Drone & Satellite Monitoring",
      icon: Cloud,
      description: "Comprehensive aerial monitoring solutions that provide high-resolution imagery and data analytics for large-scale agricultural operations.",
      benefits: [
        "Daily or weekly field monitoring without manual inspection", 
        "Plant health visualization through NDVI analysis", 
        "Automated crop damage assessment",
        "3D terrain mapping for drainage optimization"
      ]
    }
  ];

  const useCases = [
    {
      industry: "Grain",
      company: "MidwestCrops",
      challenge: "Facing declining yields due to unpredictable weather patterns and soil nutrient depletion.",
      solution: "Implemented our Precision Farming AI and Weather Analytics Platform to optimize growing conditions and anticipate weather changes.",
      results: "37% increase in average yield, 42% reduction in fertilizer usage, and successful mitigation of crop losses during two major weather events."
    },
    {
      industry: "Viticulture",
      company: "CoastVineyards",
      challenge: "Struggling with water conservation during drought conditions while maintaining grape quality.",
      solution: "Deployed our Smart Irrigation Systems with soil moisture sensors and micro-climate monitoring across all vineyard blocks.",
      results: "45% reduction in water usage, 28% improvement in grape quality metrics, and $150,000 annual savings in water costs."
    },
    {
      industry: "Dairy",
      company: "GreenPastures",
      challenge: "Needed to optimize pasture management for their grass-fed dairy operation.",
      solution: "Implemented our Drone & Satellite Monitoring with Crop Yield Prediction models to optimize grazing rotation.",
      results: "34% increase in milk production, 40% improvement in pasture utilization efficiency, and reduced supplemental feed costs by 25%."
    }
  ];

  const technologies = [
    {
      name: "Machine Learning",
      description: "Custom models for crop yield prediction, pest identification, and soil health analysis using both supervised and unsupervised techniques."
    },
    {
      name: "IoT Sensor Networks",
      description: "Low-power, long-range wireless sensors providing real-time data on soil moisture, temperature, humidity, and plant health metrics."
    },
    {
      name: "Drone & Satellite Imagery",
      description: "Multi-spectral and thermal imaging to evaluate crop health, detect stress, and identify irrigation issues across large areas."
    },
    {
      name: "Edge Computing",
      description: "On-farm data processing capabilities reducing connectivity dependencies and enabling real-time decision support even in remote areas."
    },
    {
      name: "Mobile & Web Platforms",
      description: "User-friendly interfaces providing actionable insights and control capabilities accessible from anywhere, designed for all technical skill levels."
    }
  ];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-16 mt-16">
      {/* Hero Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="grid md:grid-cols-2 gap-12 items-center mb-20"
      >
        <div className="space-y-6">
          <Badge className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200">
            Agricultural Technology
          </Badge>
          
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            AI-Powered AgriTech Solutions
          </h1>
          
          <p className="text-xl text-muted-foreground">
            Revolutionize farming operations with our cutting-edge agricultural technology solutions designed to maximize yields, conserve resources, and promote sustainable practices.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button size="lg" variant="outline" asChild>
              <Link href="/services">
                Our Services
              </Link>
            </Button>
          </div>

          <div className="pt-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm">Compatible with existing farm management systems</span>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-green-100 via-emerald-50 to-green-100 rounded-2xl p-8 shadow-lg overflow-hidden">
          <div className="absolute inset-0 bg-white/40 rounded-2xl backdrop-blur-sm" />
          {/* Interactive Dashboard */}
          <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-xl shadow-lg overflow-hidden">
            {/* Dashboard Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-500 p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Leaf className="h-6 w-6 text-white" />
                  <h3 className="font-semibold text-white">Farm Operations Dashboard</h3>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-white border-white/30 bg-white/10">
                    Live Data
                  </Badge>
                  <span className="text-white/80 text-sm flex items-center">
                    <BarChart3 className="h-3.5 w-3.5 mr-1" />
                    Updated 4m ago
                  </span>
                </div>
              </div>
            </div>
            {/* Dashboard Tabs */}
            <div className="p-1 bg-gray-100/80">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid grid-cols-3 h-8">
                  <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                  <TabsTrigger value="fields" className="text-xs">Fields</TabsTrigger>
                  <TabsTrigger value="alerts" className="text-xs">Alerts</TabsTrigger>
                </TabsList>
                {/* Overview Tab */}
                <TabsContent value="overview" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Yield Summary */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Yield Summary</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-green-500 mr-1"></div>
                            <span>Avg. Yield: 5.2 t/ha</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 mr-1"></div>
                            <span>Best Field: 7.1 t/ha</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-yellow-500 mr-1"></div>
                            <span>Below Avg: 2</span>
                          </div>
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-gray-400 mr-1"></div>
                            <span>Fields: 12</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Irrigation Metrics */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Irrigation Metrics</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span>Water Usage</span>
                            <span className="font-medium">42%</span>
                          </div>
                          <div className="w-full h-1.5 bg-green-200 rounded-full overflow-hidden">
                            <div className="bg-green-500 h-1.5" style={{ width: '42%' }} />
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span>Optimal Fields</span>
                            <span className="font-medium">9</span>
                          </div>
                          <div className="w-full h-1.5 bg-emerald-200 rounded-full overflow-hidden">
                            <div className="bg-emerald-500 h-1.5" style={{ width: '75%' }} />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Live Weather Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Live Weather Chart</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="bg-gray-100 rounded-md overflow-hidden relative h-24 flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-green-500 stroke-2 fill-green-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="agritech-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(34,197,94)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(34,197,94)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,20 Q10,15 20,18 T40,10 T60,15 T80,12 T100,20 L100,30 H0 Z" fill="url(#agritech-gradient)" />
                          <path d="M0,20 Q10,15 20,18 T40,10 T60,15 T80,12 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 24h</div>
                      </div>
                    </CardContent>
                  </Card>
                  {/* Automation Score */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm font-medium">Automation Score</CardTitle>
                        <Badge variant="outline" className="text-xs h-5 px-1.5 bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                          89/100
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1.5">
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Irrigation Automated</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-green-200 text-green-800 bg-green-50">7</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Manual Fields</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-emerald-200 text-emerald-800 bg-emerald-50">5</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Fields Tab */}
                <TabsContent value="fields" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Field Health */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Field Health</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex flex-col gap-2 text-xs">
                          <span>Healthy: <span className="font-medium text-green-600">9</span></span>
                          <span>Stressed: <span className="font-medium text-yellow-600">2</span></span>
                          <span>Critical: <span className="font-medium text-red-600">1</span></span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Soil Moisture */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Soil Moisture</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span>Avg. Moisture</span>
                            <span className="font-medium">27%</span>
                          </div>
                          <div className="w-full h-1.5 bg-green-200 rounded-full overflow-hidden">
                            <div className="bg-green-500 h-1.5" style={{ width: '27%' }} />
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span>Optimal Fields</span>
                            <span className="font-medium">8</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* NDVI Trends Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">NDVI Trends</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-3">
                      <div className="h-24 bg-gray-50 rounded-md overflow-hidden relative flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-emerald-500 stroke-2 fill-emerald-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="ndvi-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(16, 185, 129)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(16, 185, 129)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20 L100,30 H0 Z" fill="url(#ndvi-gradient)" />
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 30 days</div>
                      </div>
                    </CardContent>
                  </Card>
                  {/* Crop Types */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Crop Types</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1.5">
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Wheat</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-green-200 text-green-800 bg-green-50">5 fields</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Corn</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-emerald-200 text-emerald-800 bg-emerald-50">4 fields</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Soybean</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-yellow-200 text-yellow-800 bg-yellow-50">3 fields</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Alerts Tab */}
                <TabsContent value="alerts" className="p-3">
                  <div className="space-y-2">
                    <div className="bg-white border-l-4 border-green-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Irrigation Alert</h4>
                        <p className="text-xs text-muted-foreground">Soil moisture below threshold in Field 7. Irrigation recommended.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">20 minutes ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-yellow-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Weather Warning</h4>
                        <p className="text-xs text-muted-foreground">Frost risk detected for tonight. Protective measures advised.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">2 hours ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-red-500 rounded shadow-sm p-3 flex items-start">
                      <BarChart3 className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Pest Alert</h4>
                        <p className="text-xs text-muted-foreground">Increased pest activity detected in Corn fields. Inspection required.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">Today</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            Review Report
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            {/* Dashboard Footer */}
            <div className="bg-gray-100/80 p-2 flex justify-between items-center text-xs text-muted-foreground">
              <div>Powered by OpenLabs AI</div>
              <div className="flex items-center">
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <Zap className="h-3 w-3" /> Optimize Farm
                </Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <ArrowRight className="h-3 w-3" /> Full Dashboard
                </Button>
              </div>
            </div>
          </div>
          {/* Summary Metrics Row */}
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-green-600">35%</p>
              <p className="text-sm text-gray-600">Higher Yield</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-green-600">42%</p>
              <p className="text-sm text-gray-600">Water Saved</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-green-600">3.8x</p>
              <p className="text-sm text-gray-600">ROI</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Benefits */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="text-center mb-16"
      >
        <h2 className="text-3xl font-bold mb-12">Why Choose Our AgriTech Solutions?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-green-100 text-green-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Leaf className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Sustainable Farming</h3>
            <p className="text-gray-600">
              Data-driven approaches that optimize resource usage, reduce environmental impact, and promote long-term soil health and biodiversity.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-green-100 text-green-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Increased Productivity</h3>
            <p className="text-gray-600">
              Advanced analytics that identify yield-limiting factors and provide actionable insights to maximize production efficiency.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-green-100 text-green-700 w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Risk Mitigation</h3>
            <p className="text-gray-600">
              Predictive intelligence that helps farmers anticipate and prepare for weather events, pest outbreaks, and market fluctuations.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Solutions */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Our AgriTech Solutions</h2>
        
        <div className="grid gap-8">
          {solutions.map((solution, index) => (
            <motion.div 
              key={solution.title}
              variants={fadeIn}
              className={`flex flex-col md:flex-row gap-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
            >
              <div className="md:w-1/3 flex flex-col items-center md:items-start text-center md:text-left">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <solution.icon className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">{solution.title}</h3>
                <p className="text-gray-600 mb-4">{solution.description}</p>
              </div>
              
              <div className="md:w-2/3 bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold mb-4">Key Benefits:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {solution.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Applicable Use Cases */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-4 text-center">Applicable Use Cases</h2>
        <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
          Our AgriTech solutions can be tailored for various industry applications, providing significant advantages across different agricultural sectors.
        </p>
        <Tabs defaultValue="precision-grain-farming" className="w-full">
          <TabsList className="w-full justify-start mb-8 overflow-auto">
            <TabsTrigger value="precision-grain-farming" className="px-6">Precision Grain Farming</TabsTrigger>
            <TabsTrigger value="smart-viticulture" className="px-6">Smart Viticulture</TabsTrigger>
            <TabsTrigger value="dairy-pasture-optimization" className="px-6">Dairy Pasture Optimization</TabsTrigger>
          </TabsList>
          {/* Precision Grain Farming Use Case */}
          <TabsContent value="precision-grain-farming" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Grain</Badge>
                <h3 className="text-2xl font-semibold mb-4">Precision Grain Farming</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Declining yields due to unpredictable weather patterns and soil nutrient depletion.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Precision farming AI for yield optimization</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Weather analytics for planting and harvesting timing</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Soil health monitoring and nutrient management</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated pest and disease detection</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Up to 37% increase in average yield, 42% reduction in fertilizer usage, and successful mitigation of crop losses during major weather events.</p>
                  </div>
                </div>
                <Button variant="link" className="mt-6 pl-0">
                  Learn More About Precision Grain Farming <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Leaf className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Precision Farming AI</h5>
                        <p className="text-sm text-gray-600">AI-driven crop management and yield optimization with real-time data.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <CloudRain className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Weather Analytics Platform</h5>
                        <p className="text-sm text-gray-600">Hyperlocal weather forecasting and impact modeling for agriculture.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">
                        Compatible with existing farm management systems
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          {/* Smart Viticulture Use Case */}
          <TabsContent value="smart-viticulture" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Viticulture</Badge>
                <h3 className="text-2xl font-semibold mb-4">Smart Viticulture</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Water conservation during drought conditions while maintaining crop quality.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Smart irrigation systems with real-time soil moisture sensors</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Micro-climate monitoring and weather alerts</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated scheduling and water conservation analytics</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Quality monitoring for grape production</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Up to 45% reduction in water usage, 28% improvement in crop quality metrics, and significant annual savings in water costs.</p>
                  </div>
                </div>
                <Button variant="link" className="mt-6 pl-0">
                  Learn More About Smart Viticulture <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Droplet className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Smart Irrigation Systems</h5>
                        <p className="text-sm text-gray-600">IoT-powered irrigation with real-time soil and weather data integration.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Sun className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Micro-Climate Monitoring</h5>
                        <p className="text-sm text-gray-600">Field-level weather and environmental monitoring for quality control.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">
                        Compatible with existing farm management systems
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          {/* Dairy Pasture Optimization Use Case */}
          <TabsContent value="dairy-pasture-optimization" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Dairy</Badge>
                <h3 className="text-2xl font-semibold mb-4">Dairy Pasture Optimization</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Optimizing pasture management for grass-fed dairy operations to increase productivity and reduce costs.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Drone & satellite monitoring for pasture management</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Crop yield prediction for feed planning</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated rotation and grazing optimization</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Real-time health and productivity analytics</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Up to 34% increase in milk production, 40% improvement in pasture utilization efficiency, and reduced supplemental feed costs by 25%.</p>
                  </div>
                </div>
                <Button variant="link" className="mt-6 pl-0">
                  Learn More About Dairy Pasture Optimization <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Cloud className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Drone & Satellite Monitoring</h5>
                        <p className="text-sm text-gray-600">Aerial imagery and analytics for pasture and crop management.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <LineChart className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Crop Yield Prediction</h5>
                        <p className="text-sm text-gray-600">Machine learning models for accurate feed and yield forecasting.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">
                        Compatible with existing farm management systems
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </motion.section>

      {/* Technology Stack */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-6 text-center">Technology Stack</h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
          Our AgriTech solutions leverage cutting-edge technologies to transform traditional farming into data-driven precision agriculture.
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {technologies.map((tech) => (
            <Card key={tech.name} className="bg-white">
              <CardHeader>
                <CardTitle>{tech.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {tech.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl p-12 text-center"
      >
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Agricultural Operations?</h2>
        <p className="max-w-2xl mx-auto mb-8">
          Schedule a personalized demo to see how our AgriTech solutions can increase yields, reduce costs, and promote sustainable farming practices for your specific crops and growing conditions.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10" asChild>
            <Link href="/contact">
              Talk to Sales
            </Link>
          </Button>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Is this suitable for small-scale farming operations?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Absolutely. We offer scalable solutions that can be configured for farms of all sizes, from small family operations to large commercial enterprises. Our entry-level packages are specifically designed to be accessible and impactful for smaller farms.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>What kind of technical expertise is required?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Our solutions are designed with user-friendly interfaces that require minimal technical expertise. We provide comprehensive training and ongoing support, and our mobile apps are intuitive enough for users of all technical skill levels.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>How long does implementation typically take?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Basic implementations can be completed in 2-3 weeks. More comprehensive solutions involving extensive sensor networks may take 4-8 weeks depending on the size and complexity of your operation. We work with your growing season to ensure minimal disruption.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Do these solutions work in areas with limited connectivity?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Yes, many of our solutions utilize edge computing technology and can function with intermittent connectivity. Data is stored locally and synced when connectivity is available, ensuring you never lose critical insights even in remote areas.
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.section>
    </div>
  );
} 